import axios from "axios";

// Where a refresh token was kept before the shared session; only read to trade it for one.
export const TOKEN_KEY = process.env.REACT_APP_ARBO_TOKEN;
export const ARBO_PROFILE_KEY = process.env.REACT_APP_ARBO_PROFILE_KEY;
export const ARBO_SETTINGS_KEY = process.env.REACT_APP_ARBO_SETTINGS_KEY;
/**
 * What signs a reader in is the API's session, an HttpOnly cookie shared with the ArView pages, so
 * signing in or out on either side counts on both. The stored profile is this tab's note of it,
 * confirmed against the session on startup and whenever the tab comes back (see Arbo.jsx).
 */
export const isAuthenticated = () => localStorage.getItem(ARBO_PROFILE_KEY);
export const MVT_PATHS = ["/mvt/liquids/station/", "/mvt/layers/records/"];
export const STRIPE_CHECKOUT_URL = process.env.REACT_APP_STRIPE_CHECKOUT_URL;

let accessToken = null;
let refreshTokenPromise = null;

const API_URL = `${process.env.REACT_APP_API_BASE_URL}api`;

axios.defaults.baseURL = `${API_URL}/`;

const refreshInstance = axios.create({
    baseURL: API_URL,
});

// The session endpoints (/api/auth/): the cookie goes along, and errors arrive as axios gives them.
const sessionInstance = axios.create({
    baseURL: API_URL,
    withCredentials: true,
});

// For the other calls that start the session (a magic link) or must keep it (a password change).
const WITH_SESSION = { withCredentials: true };

// Query param Login reads to send the reader back where they were (see pages/User/Login.jsx).
export const LOGIN_NEXT_PARAM = "next";

const isLoginPath = (pathname = "") => /(^|\/)login(\/|$)/.test(pathname);

// Login URL that remembers `from` (a location) so the reader lands back there after signing in.
export const getLoginUrl = (from, loginPath = "/login") => {
    const next = from ? `${from.pathname}${from.search || ""}${from.hash || ""}` : "";
    if (!next || next === "/" || isLoginPath(from.pathname)) {
        return loginPath;
    }
    return `${loginPath}?${LOGIN_NEXT_PARAM}=${encodeURIComponent(next)}`;
};

// Where to go after signing in: the `next` param when it is a same-origin, non-login path, otherwise `fallback`.
export const getPostLoginUrl = (location, fallback) => {
    const next = new URLSearchParams(location?.search).get(LOGIN_NEXT_PARAM);
    const isSafe = next && /^\/(?![/\\])/.test(next) && !isLoginPath(next.split(/[?#]/)[0]);
    return isSafe ? next : fallback;
};

let leavingForLogin = false;

// Only the first call navigates: requests in flight fail together, and each later
// `location.replace` would win over the one before it.
const leaveForLogin = (url) => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ARBO_PROFILE_KEY);
    if (leavingForLogin) return;
    leavingForLogin = true;
    window.location.replace(url);
};

// Ends the session as well, so the ArView pages sign out too.
export const logout = () =>
    sessionInstance
        .post("/auth/logout/", {})
        .catch(() => {})
        .then(() => leaveForLogin("/login"));

/**
 * The session ran out under the reader, as opposed to them signing out. A full page load drops
 * router state, so the page they were on rides along in the query string instead.
 */
const expireSession = () => leaveForLogin(getLoginUrl(window.location));

const getToken = () => {
    return accessToken;
};

export const getUserProfile = () => {
    try {
        return JSON.parse(localStorage.getItem(ARBO_PROFILE_KEY));
    } catch (e) {
        expireSession();
    }
};
export const getReferralsUrl = () => JSON.parse(localStorage.getItem(ARBO_PROFILE_KEY))?.referrals_url;

export const useIsFirstLogin = () => {
    const userProfile = getUserProfile();
    if (userProfile?.attributes) {
        const { last_login, first_login } = userProfile?.attributes;
        return last_login === first_login;
    }

    return false;
};

export const getAuthorizationHeaders = () => {
    const accessToken = getToken();
    return { authorization: `Bearer ${accessToken}` };
};

const readStoredProfile = () => {
    try {
        return JSON.parse(localStorage.getItem(ARBO_PROFILE_KEY));
    } catch (e) {
        return null;
    }
};

/**
 * A fresh access token from the session. A reader still holding a refresh token from before the
 * shared session trades it for a session, once; drop that branch once those tokens have expired
 * (14 days after the shared session ships).
 */
const mintAccessToken = () =>
    sessionInstance
        .post("/auth/token/", {})
        .catch((error) => {
            const legacyRefresh = localStorage.getItem(TOKEN_KEY);
            if (error.response?.status !== 401 || !legacyRefresh) throw error;
            localStorage.removeItem(TOKEN_KEY);
            return sessionInstance.post("/auth/upgrade/", { refresh: legacyRefresh });
        })
        .then((response) => {
            const before = readStoredProfile();
            const data = handleTokenResponse(response);
            // Someone else signed in on the ArView pages: everything this tab holds is the last reader's.
            if (before && before.id !== data.profile?.id) window.location.reload();
            return data;
        });

function refreshToken(cb) {
    if (isAuthenticated()) {
        refreshTokenPromise = mintAccessToken()
            .catch(() => {
                expireSession();
            })
            .then((data) => data?.access)
            .then(cb);
    } else {
        expireSession();
    }

    return refreshTokenPromise;
}

/**
 * On startup, for a reader with nothing stored here: they may have signed in on the ArView pages.
 * Resolves whether they had. Never navigates.
 */
export const restoreSession = () =>
    mintAccessToken().then(
        () => true,
        () => false,
    );

const RECHECK_MIN_INTERVAL_MS = 10 * 1000;
let lastRecheck = 0;

/**
 * When the tab comes back into view: the reader may have signed out, or in as someone else, on
 * the ArView pages meanwhile, and the access token would otherwise carry on for up to 15 minutes.
 * Focus and visibility both fire on the way back, hence the interval.
 */
export const recheckSession = () => {
    if (!isAuthenticated() || document.visibilityState === "hidden") return;
    const now = Date.now();
    if (now - lastRecheck < RECHECK_MIN_INTERVAL_MS) return;
    lastRecheck = now;
    mintAccessToken().catch((error) => {
        if (error.response?.status === 401) expireSession();
    });
};

axios.interceptors.request.use(async (config) => {
    let token = accessToken;
    if (!token) {
        token = refreshTokenPromise ? await refreshTokenPromise : await refreshToken();
        refreshTokenPromise = null;
    }

    config.headers.Authorization = `Bearer ${token}`;

    return config;
});

refreshInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error && error.response) {
            switch (error.response.status) {
                case /^5\d{2}/.test(error.response.status): {
                    return Promise.reject(error);
                }
                default: {
                    return Promise.reject(error.response.data);
                }
            }
        } else {
            return Promise.reject("Network error");
        }
    },
);

axios.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response ? error.response.status : null;
        if (status === 401 && error.config.isRetry) {
            expireSession();
            // Retry only once, it prevents infinite loops
            return Promise.reject("Auth error");
        }
        if (status === 401 && !error.config.isRetry) {
            return refreshToken(() => {
                return axios.request({ ...error.config, isRetry: true }); // Re try
            });
        } else if (error && error.response) {
            switch (error.response.status) {
                case /^5\d{2}/.test(error.response.status): {
                    return Promise.reject(error);
                }
                default: {
                    return Promise.reject(error.response.data);
                }
            }
        } else {
            return Promise.reject(error);
        }
    },
);

// Every sign-in ends here. Any refresh token in the response is left alone: the session keeps the
// reader signed in, and only other clients use those.
const handleTokenResponse = (response) => {
    if (response?.data?.access) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.setItem(
            ARBO_PROFILE_KEY,
            JSON.stringify({
                ...response.data.profile,
                subscription: response.data.subscription,
            }),
        );
        accessToken = response.data.access;
    }
    return response.data;
};

const login = (email, password) =>
    refreshInstance
        .post("/auth/login/", { username: email, password: password }, WITH_SESSION)
        .then(handleTokenResponse);

const userRegister = ({ email, password, firstName, lastName, company, phone, ref, jobRole, plan, prices }) => {
    const params = {
        email: email.toLowerCase(),
        first_name: firstName,
        last_name: lastName,
        job_role: jobRole,
        password,
        plan,
        prices,
        phone_number: phone,
        company_name: company,
        ref,
    };
    return refreshInstance.post("/register/", params).then(({ data }) => data);
};

const passwordReset = async ([new_password1, new_password2]) =>
    await axios.post(`/password/change/`, { new_password1, new_password2 }, WITH_SESSION);

const phoneLogin = (email) => refreshInstance.post("/users/phone/login", { email }).then(({ data }) => data);

const phoneVerify = async ({ phone_number, session_token, security_code }) => {
    return refreshInstance
        .post("/users/phone/verify", { phone_number, session_token, security_code })
        .then(handleTokenResponse);
};

const emailInvite = async ({ emails }) => axios.post("/invite/", { emails }).then(({ data }) => data);
const emailShare = async ({ emails }) => axios.post("/share/", { emails }).then(({ data }) => data);

const emailLogin = (email) => refreshInstance.post("/users/magic-link/login", { email }).then(({ data }) => data);
const forgotPassword = (email) =>
    refreshInstance.post("/users/magic-link/forgot_password", { email }).then(({ data }) => data);
const emailVerify = async ({ email, key }) =>
    refreshInstance.post("/users/magic-link/verify", { email, key }, WITH_SESSION).then(handleTokenResponse);

const patchUserProfile = async ({ id, data }) => {
    const response = await axios.patch(`/users/profiles/${id}`, data);
    const response_data = response?.data ? response.data : {};
    const profile = getUserProfile();
    const newData = { ...response_data, subscription: profile.subscription };
    localStorage.setItem(ARBO_PROFILE_KEY, JSON.stringify(newData));
    return response.data;
};

export const patchUserAttributes = async (data = {}) => {
    const { id, attributes } = getUserProfile();
    return await patchUserProfile({
        id,
        data: {
            attributes: {
                ...attributes,
                ...data,
            },
        },
    });
};

export const patchUser = async (data = {}) => {
    const { id } = getUserProfile();
    return await patchUserProfile({
        id,
        data,
    });
};

const getAccounts = async () => {
    const response = await axios.get("/users/account");
    return response.data;
};
const getAccount = async (id) => await axios.get(`/users/account/${id}`)?.data;
const updateAccount = async ({ id, name, description }) => {
    const response = await axios.patch(`/users/account/${id}`, {
        name,
        description,
    });
    return response.data;
};

const getInviteLinks = async () => await axios.get("/users/account-invite-link");

const createInviteLinks = async ({ id, email, first_name, last_name }) => {
    const response = await axios.post("/users/account-invite-link", {
        id,
        email,
        first_name,
        last_name,
    });
    return response.data;
};

const verifyInviteLink = async ({ account, key, email }) =>
    await refreshInstance
        .post("/users/account-invite-link/verify", {
            account,
            email,
            key,
        })
        .then(handleTokenResponse);

const deleteInviteLink = async (id) => await axios.delete(`/users/account-invite-link/${id}`);
const getJobRoles = async () => await refreshInstance.get(`/users/role`);

const contactSupport = async ({ message }) => {
    const response = await axios.post("/contact-support/", { message });
    await axios.post("/support/ticket/", { content: message });

    return response;
};

export const getLocalPreferences = async (id) => {
    const store = JSON.parse(localStorage.getItem(ARBO_SETTINGS_KEY)) || {};
    return id ? store[id] : store;
};

export const saveLocalPreferences = async ({ id, data }) => {
    const store = getLocalPreferences();
    const newStore = { ...store };
    newStore[id] = data;
    localStorage.setItem(ARBO_SETTINGS_KEY, JSON.stringify(newStore));
    return newStore;
};

export const setFeatureCount = (id) => {
    const { attributes } = getUserProfile();
    const count = +attributes[id] || 0;

    const data = {};
    data[id] = count + 1;
    patchUserAttributes(data);
    return data[id];
};

export const userHasGroup = (group) => getUserProfile()?.groups?.find((d) => d.name === group);

export const getFeatureCount = (id) => {
    const { attributes } = getUserProfile();
    const count = +attributes[id] || 0;

    return count;
};

export default {
    contactSupport,
    createInviteLinks,
    deleteInviteLink,
    emailInvite,
    emailLogin,
    emailShare,
    emailVerify,
    forgotPassword,
    getAccount,
    getAccounts,
    getFeatureCount,
    getInviteLinks,
    getLocalPreferences,
    getUserProfile,
    login,
    logout,
    passwordReset,
    patchUserAttributes,
    patchUserProfile,
    phoneLogin,
    phoneVerify,
    saveLocalPreferences,
    setFeatureCount,
    updateAccount,
    userRegister,
    userHasGroup,
    verifyInviteLink,
    getJobRoles,
};
